/**
 * Custom JavaScript Enhancements & DOM Sanitization
 * Store: Georgina Westley Art & Design
 * This file contains bespoke scripts to keep core Dawn scripts pristine.
 */

// Clean up accidental empty, whitespace-only, or fragmented links in rich text content (e.g. blog/page RTE)
function cleanOrphanedLinks() {
  // Remove accidental browser extension artifacts copied into rich text (e.g. Google Docs/Gemini selection tools)
  document.querySelectorAll('button.mat-mdc-tooltip-trigger, .selection-menu-button, [data-test-id="tunable-modify-selection-button"], [class*="mat-mdc-"]').forEach((el) => {
    el.remove();
  });

  document.querySelectorAll('.rte a, .article-template__content a').forEach((link) => {
    if (link.querySelector('img, svg, picture, video, iframe')) return;

    // Merge adjacent links with identical href (e.g. <a href="url">@</a><a href="url">username</a>)
    let next = link.nextSibling;
    while (next && next.nodeType === Node.TEXT_NODE && next.textContent.trim() === '') {
      next = next.nextSibling;
    }
    if (next && next.tagName === 'A' && next.getAttribute('href') === link.getAttribute('href')) {
      next.prepend(...link.childNodes);
      link.remove();
      return;
    }

    const cleanText = link.textContent.replace(/[\s\u00A0\u200B\u200C\u200D\uFEFF]/g, '');

    // If link is only "@" and immediately precedes a handle/word, merge the word into the link
    if (cleanText === '@' && link.nextSibling && link.nextSibling.nodeType === Node.TEXT_NODE) {
      const match = link.nextSibling.textContent.match(/^\s*([a-zA-Z0-9._-]+)(.*)/s);
      if (match) {
        link.textContent = '@' + match[1];
        link.nextSibling.textContent = match[2];
        return;
      }
    }

    // Unwrap empty, whitespace-only, or isolated punctuation links
    if (cleanText === '' || /^[,.;:!?]+$/.test(cleanText)) {
      link.replaceWith(...link.childNodes);
    }
  });
}

// Add external link icon and accessible screen-reader cue to links opening in a new window/tab
function addExternalLinkCues() {
  const localizedMsg = document.getElementById('a11y-new-window-message')?.textContent?.trim();
  const cueText = localizedMsg ? `(${localizedMsg})` : '(opens in a new window)';

  document.querySelectorAll('a[target="_blank"]').forEach((link) => {
    if (
      link.dataset.hasExternalCue ||
      link.querySelector('.icon-external-link, .visually-hidden') ||
      link.closest('.list-social, .social-icons, .footer__list-social, .header__icon, .cart-count-bubble')
    ) {
      return;
    }

    if (link.querySelector('img, svg, picture, video, iframe, canvas')) {
      return;
    }

    const hasText =
      link.textContent.trim() ||
      link.getAttribute('aria-label')?.trim() ||
      link.getAttribute('title')?.trim();
    if (!hasText) {
      return;
    }

    link.dataset.hasExternalCue = 'true';

    const existingAriaLabel = link.getAttribute('aria-label')?.trim();
    if (existingAriaLabel && !/new\s+(window|tab)/i.test(existingAriaLabel)) {
      link.setAttribute('aria-label', `${existingAriaLabel} ${cueText}`);
    }

    const srSpan = document.createElement('span');
    srSpan.className = 'visually-hidden';
    srSpan.textContent = ` ${cueText}`;

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'icon icon-external-link');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('viewBox', '0 0 12 12');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '12');
    svg.innerHTML = '<path fill="currentColor" d="M1.5 1A1.5 1.5 0 0 0 0 2.5v8A1.5 1.5 0 0 0 1.5 12h8a1.5 1.5 0 0 0 1.5-1.5V7a.5.5 0 0 0-1 0v3.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-8a.5.5 0 0 1 .5-.5H5a.5.5 0 0 0 0-1H1.5zM7.5.5a.5.5 0 0 0 0 1h2.793L5.146 6.646a.5.5 0 1 0 .708.708L11 2.207V5a.5.5 0 0 0 1 0V.5a.5.5 0 0 0-.5-.5h-4z"/>';

    link.appendChild(srSpan);
    link.appendChild(svg);
  });
}

function initCustomEnhancements() {
  cleanOrphanedLinks();
  addExternalLinkCues();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomEnhancements);
} else {
  initCustomEnhancements();
}

window.addEventListener('load', addExternalLinkCues);

if (window.MutationObserver) {
  let customDebounceRaf;
  const customObserver = new MutationObserver(() => {
    if (customDebounceRaf) cancelAnimationFrame(customDebounceRaf);
    customDebounceRaf = requestAnimationFrame(() => {
      addExternalLinkCues();
    });
  });
  customObserver.observe(document.documentElement, { childList: true, subtree: true });
}
